import React, { useState, useEffect } from 'react';
import { Search, Filter, User, Activity } from 'lucide-react';
import auditService from '../services/auditService';
import { formatDateTime } from '../utils/dateUtils';

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    action: '',
    status: '',
    user_id: '',
    start_date: '',
    end_date: '',
    search: ''
  });

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      setLoading(true);
      // Remove empty filters
      const activeFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value) acc[key] = value;
        return acc;
      }, {});
      
      const data = await auditService.getLogs(activeFilters);
      setLogs(data);
      setError('');
    } catch (err) {
      setError('Failed to load audit logs: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    loadLogs();
  };

  const handleReset = () => {
    setFilters({
      action: '',
      status: '',
      user_id: '',
      start_date: '',
      end_date: '',
      search: ''
    });
    // Reload will happen on next render due to useEffect
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'failure':
        return 'bg-red-100 text-red-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-virgo-navy-800 text-virgo-teal-100';
    }
  };

  const getActionColor = (action) => {
    if (action?.includes('DELETE')) return 'text-red-600';
    if (action?.includes('CREATE')) return 'text-green-600';
    if (action?.includes('UPDATE')) return 'text-virgo-teal-400';
    if (action?.includes('LOGIN')) return 'text-cyan-600';
    return 'text-virgo-teal-300';
  };

  const formatDate = (dateString) => {
    return formatDateTime(dateString);
  };

  const formatDetails = (details) => {
    if (!details) return '-';
    if (typeof details === 'string') {
      try {
        details = JSON.parse(details);
      } catch {
        return details;
      }
    }
    return JSON.stringify(details, null, 2);
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-virgo-teal-400">Loading audit logs...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-virgo-gold-400">Audit Logs</h1>
        <p className="text-virgo-teal-300 mt-1">View and monitor all system activity</p>
      </div>

      {/* Filters */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-virgo-teal-400" />
          <h2 className="text-lg font-semibold text-white">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-virgo-teal-500" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search details..."
                className="w-full pl-9 pr-3 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>
          </div>

          {/* Action */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Action
            </label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full px-3 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
            >
              <option value="">All Actions</option>
              <option value="LOGIN">Login</option>
              <option value="LOGOUT">Logout</option>
              <option value="FILE_UPLOAD">File Upload</option>
              <option value="FILE_DOWNLOAD">File Download</option>
              <option value="FILE_DELETE">File Delete</option>
              <option value="USER_CREATE">User Create</option>
              <option value="USER_UPDATE">User Update</option>
              <option value="USER_DELETE">User Delete</option>
              <option value="PASSWORD_RESET">Password Reset</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
            >
              <option value="">All Statuses</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILURE">Failure</option>
              <option value="WARNING">Warning</option>
            </select>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="w-full px-3 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={handleSearch}
            className="flex items-center gap-2 px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition-colors"
          >
            <Search className="w-4 h-4" />
            Apply Filters
          </button>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 border border-virgo-teal-700 text-virgo-teal-200 rounded-lg hover:bg-virgo-navy-600 transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Logs Table */}
      <div className="bg-virgo-navy-700 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-virgo-navy-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Resource
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  IP Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-virgo-navy-700 divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-virgo-navy-600">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {formatDate(log.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-virgo-teal-500" />
                      {log.user_id || 'System'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getActionColor(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(log.status)}`}>
                      {log.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                    {log.resource_type ? (
                      <div>
                        <div className="font-medium">{log.resource_type}</div>
                        {log.resource_id && (
                          <div className="text-xs text-virgo-teal-500">ID: {log.resource_id}</div>
                        )}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                    {log.ip_address || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-virgo-teal-400">
                    <details className="cursor-pointer">
                      <summary className="text-virgo-teal-400 hover:text-virgo-teal-200">View</summary>
                      <pre className="mt-2 text-xs bg-virgo-navy-600 p-2 rounded max-w-xs overflow-x-auto">
                        {formatDetails(log.details)}
                      </pre>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {logs.length === 0 && !loading && (
          <div className="text-center py-12">
            <Activity className="mx-auto h-12 w-12 text-virgo-teal-500" />
            <h3 className="mt-2 text-sm font-medium text-white">No audit logs found</h3>
            <p className="mt-1 text-sm text-virgo-teal-400">
              {Object.values(filters).some(v => v) 
                ? 'Try adjusting your filters' 
                : 'System activity will appear here'}
            </p>
          </div>
        )}
      </div>

      {/* Stats footer */}
      {logs.length > 0 && (
        <div className="mt-4 text-sm text-virgo-teal-300">
          Showing {logs.length} log{logs.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

export default AuditLogs;
